import { CircleCloseIcon, DiscountWhatsNewIcon, ExternalLinkIcon, OutlinedInfoIcon, WhatsNewIcon } from 'assets/icons';
import { CustomButton } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import CustomModal from 'components/custom-modal/CustomModal';
import { INVOICE_DISCOUNT_CALCULATION_LEARN_MORE_URL } from 'config/index';
import { FC, KeyboardEvent } from 'react';
import { VoidFn } from 'types/baseTypes';
import { openInNewTab } from 'utils/commonUtils';

interface WhatsNewModalProps {
  config: IWhatsNewConfig;
  onPrimaryBtnClick: VoidFn;
  onClose: VoidFn;
  className?: string;
}

export interface IWhatsNewConfig {
  open: boolean;
}

const WhatsNewModal: FC<WhatsNewModalProps> = ({ onPrimaryBtnClick, config, onClose = undefined, className = '' }) => {
  const handleLearnMoreClick = () => {
    openInNewTab(INVOICE_DISCOUNT_CALCULATION_LEARN_MORE_URL);
  };

  const handleLearnMoreKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      handleLearnMoreClick();
    }
  };

  return (
    <div className={`${className}`}>
      <CustomModal
        enableOutsideClick={false}
        closeModal={onClose && onClose}
        open={config.open}
        backgroundColor="bg-primary"
        height="h-auto max-h-[90%]"
        width="w-[90%] max-w-[482px] md:w-[482px]"
        className="overflow-auto">
        <div className="flex h-full w-full grow flex-col">
          <div className="customTransparentScroll flex h-full w-full flex-col overflow-auto p-6">
            <CircleCloseIcon className="ml-auto shrink-0 cursor-pointer justify-end" onClick={onClose} />
            <WhatsNewIcon className="mx-auto mb-4 h-[60px] w-[60px] shrink-0" />
            <div className="mx-auto mb-4 text-xl font-bold text-heading">What's New</div>
            <div className="rounded-md border px-3 py-4">
              <div className="flex items-start gap-5">
                <DiscountWhatsNewIcon className="h-10 w-10 shrink-0" />
                <div className="flex flex-col gap-4">
                  <p className="text-17px font-semibold leading-snug text-heading">
                    Apply Discounts Before or After Tax
                  </p>
                  <div className="flex flex-col gap-4 pr-5">
                    <p className="text-sbase font-normal text-primaryText">
                      You can now choose whether discounts apply before or after tax on your invoices and estimates.
                    </p>
                    <p className="text-sbase font-normal text-primaryText">
                      Before-tax discounts reduce the taxable amount, giving customers greater savings. Use after-tax
                      for manufacturer rebates or instant rebates where the issuer sets the terms.
                    </p>
                  </div>

                  <hr className="border-borderGray" />
                  <div className="flex items-start gap-2 text-sbase font-normal text-primaryText">
                    <OutlinedInfoIcon className="path-fill-current mt-0.5 h-4 w-4 shrink-0 text-primaryText" />
                    <p>
                      Set a default for your whole team in <span className="font-semibold">Team Settings</span> →{' '}
                      <span className="font-semibold">Invoices</span> → <span className="font-semibold">Discounts</span>
                      . Any team member can override it when adding a discount to a specific invoice.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div
              role="button"
              tabIndex={0}
              aria-label="Learn more about discount calculations"
              onClick={handleLearnMoreClick}
              onKeyDown={handleLearnMoreKeyDown}
              className="mx-auto mt-5 flex cursor-pointer items-center gap-1.5">
              <span className="text-sbase font-semibold text-secondary">Learn more</span>
              <ExternalLinkIcon className="path-fill-current shrink-0 text-secondary" />
            </div>
            <CustomButton
              className="mt-4 h-10 w-full shrink-0"
              id="whats-new-primary-btn"
              type={ButtonType.PRIMARY}
              onClick={onPrimaryBtnClick}>
              Got it
            </CustomButton>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default WhatsNewModal;
